import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  DEFAULT_WATERMARK,
  shopWatermarkSchema,
  type WatermarkPosition,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import type { WatermarkSpec } from './image-prep.js';

/**
 * The shop's watermark, in the shape the image pipeline burns in.
 *
 * `null` unless the shop has switched one on AND given it words — a watermark with no text is
 * not a faint mark, it is nothing, and passing one through would cost a canvas pass per upload
 * to draw an empty string.
 *
 * Read from the settings endpoint every screen already reads, and cached hard: this changes
 * when an owner changes it, and asking again on every upload would spend a request per
 * photograph on an answer that is the same all day.
 */

/** Only the section this needs. The settings response is fat; parsing all of it here is not. */
const watermarkSettingsSchema = z.object({
  watermark: shopWatermarkSchema
    .transform((value) => ({
      enabled: value.enabled ?? DEFAULT_WATERMARK.enabled,
      text: value.text ?? DEFAULT_WATERMARK.text,
      position: (value.position ?? DEFAULT_WATERMARK.position) as WatermarkPosition,
      opacity: value.opacity ?? DEFAULT_WATERMARK.opacity,
    }))
    .optional(),
});

export function useShopWatermark(enabled = true): WatermarkSpec | null {
  const client = useApiClient();
  const { data } = useQuery({
    queryKey: ['settings', 'watermark'],
    queryFn: () => client.get('/settings', { schema: watermarkSettingsSchema }),
    enabled,
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const watermark = data?.watermark;
  if (!watermark?.enabled) return null;
  const text = watermark.text?.trim();
  if (!text) return null;

  return { text, position: watermark.position, opacity: watermark.opacity };
}
